import { LensFlareEffect } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import {
  Color,
  Uniform,
  Vector2,
  Vector3,
  type Texture,
  type WebGLRenderer,
  type WebGLRenderTarget,
} from 'three';

import { POST_DEFAULTS, type PostState } from '../skyStore';

export class SunLensFlareEffect extends LensFlareEffect {
  readonly source = new Vector3();
  readonly resolution = new Vector2(1, 1);
  private readonly cloudMap = new Uniform<Texture | null>(null);
  private readonly hasClouds = new Uniform(false);

  constructor(private readonly getCloudMap: () => Texture | null) {
    super({
      blendFunction: BlendFunction.NORMAL,
      enabled: true,
      glareSize: POST_DEFAULTS.flareGlareSize,
      lensPosition: new Vector3(),
      screenRes: new Vector2(1, 1),
      starPoints: POST_DEFAULTS.flareStarPoints,
      flareSize: 0.005,
      flareSpeed: 0,
      flareShape: 0.01,
      animated: false,
      anamorphic: POST_DEFAULTS.flareAnamorphic,
      colorGain: new Color(12, 10, 8),
      lensDirtTexture: null,
      haloScale: 0.5,
      secondaryGhosts: POST_DEFAULTS.flareGhosts,
      aditionalStreaks: false,
      ghostScale: POST_DEFAULTS.flareGhostSize,
      opacity: 1,
      starBurst: false,
    });
    this.uniforms.get('lensPosition')!.value = this.source;
    this.uniforms.get('screenRes')!.value = this.resolution;
    this.uniforms.set('flareCloudMap', this.cloudMap);
    this.uniforms.set('flareHasClouds', this.hasClouds);
    const shader = this.getFragmentShader();
    const output = 'mix(finalColor, vec3(.0), opacity)';
    if (!shader.includes(output))
      throw new Error('Lens flare shader output changed');
    const visibility = `
      float flareVisibility() {
        if (!flareHasClouds) return 1.0;
        vec2 sunUv = lensPosition * 0.5 + 0.5;
        vec2 texel = 2.0 / screenRes;
        float alpha = texture2D(flareCloudMap, sunUv).a;
        alpha += texture2D(flareCloudMap, sunUv + vec2(texel.x, 0.0)).a;
        alpha += texture2D(flareCloudMap, sunUv - vec2(texel.x, 0.0)).a;
        alpha += texture2D(flareCloudMap, sunUv + vec2(0.0, texel.y)).a;
        alpha += texture2D(flareCloudMap, sunUv - vec2(0.0, texel.y)).a;
        return clamp(1.0 - alpha / 5.0, 0.0, 1.0);
      }
    `;
    this.setFragmentShader(`
      uniform sampler2D flareCloudMap;
      uniform bool flareHasClouds;
      ${shader
        .replace('void mainImage(', `${visibility}\nvoid mainImage(`)
        .replace(
          'vec2 myUV = uv -0.5;',
          'if (!enabled) { outputColor = inputColor; return; }\nvec2 myUV = uv -0.5;'
        )
        .replace(output, '(finalColor * (1.0 - opacity) * flareVisibility())')}
    `);
  }

  setAppearance(settings: PostState): void {
    this.uniforms.get('secondaryGhosts')!.value = settings.flareGhosts;
    this.uniforms.get('ghostScale')!.value = settings.flareGhostSize;
    this.uniforms.get('glareSize')!.value = settings.flareGlareSize;
    this.uniforms.get('starPoints')!.value = settings.flareStarPoints;
    this.uniforms.get('anamorphic')!.value = settings.flareAnamorphic;
  }

  set visibility(value: number) {
    // The upstream shader's opacity is inverted: one hides the flare.
    this.uniforms.get('opacity')!.value = 1 - value;
    this.uniforms.get('enabled')!.value = value > 0.001;
  }

  override update(
    renderer: WebGLRenderer,
    input: WebGLRenderTarget,
    delta: number
  ): void {
    super.update(renderer, input, delta);
    this.cloudMap.value = this.getCloudMap();
    this.hasClouds.value = this.cloudMap.value !== null;
  }
}
